import {useEffect, useState} from "react";
import {activity_types} from "./activity_types";
import {  ArrowLeftIcon, CheckCircleIcon, CheckIcon, WarningIcon } from "@chakra-ui/icons";
import { Box,Heading, Tooltip, Flex, HStack, Accordion, AccordionButton, AccordionIcon, 
  AccordionPanel, AccordionItem } from "@chakra-ui/react"
import {map as m,filter as f, prop as p, head,compose as C,groupBy as g, includes,__,maxBy,countBy,
  identity as id, chain,toPairs,last,propEq,is,reduce,sort, ascend,path,
  addIndex,curry,findIndex,slice,length,keys,dec,uniq} from "ramda";
import { useSelector } from "react-redux";
import { PartHeader } from "./part_header";


const responded = (a,props,input) => C(
  length,uniq,m(p("participant_id")),
  f(x => x.activity_id == a.id && (input ? x.input_id == input : true))
)(props.data)


export const Activity = (props) => {
  const lang = useSelector(path(["ui","lang"])); 
  const has_responded = props.data.filter(x => 
    (props.activity.is_private ? x.participant_id === props.participant_id : true) && 
    x.activity_id === props.activity.id).length;
  const all_done = props.activity.is_private ? props.answers_needed === props.responded : has_responded;
  return <Box mt={5} shadow="xs" p={5} borderRadius={5} bg={props.activity.is_private ?  "gray.300" : "white"}>
    <HStack>
      <Heading as='h3' size='sm'>{lang === "en" ? props.activity.name_eng : props.activity.name }</Heading>
      {all_done ? <CheckCircleIcon color="green"></CheckCircleIcon> :
      has_responded ? <CheckIcon color="green"></CheckIcon> : null}
    </HStack>
    <p style={{whiteSpace:'pre-wrap'}}>{lang === "en" ? props.activity.description_eng : props.activity.description }</p>
    {activity_types(props.activity.type,{...props,all_done,
      is_private:props.activity.is_private,activity_id: props.activity.id,
    })}
  </Box>
}

const filter_data = (a,props,input) => C(
  sort(ascend(p("id"))),
  f(x => 
  (a.is_private ? x.participant_id == props.participant_id : true) && 
  x.activity_id == a.id && 
  (input ? x.input_id == input : true))
)(props.data)

export const Regular = (props) => {
  const items = C(
    sort(ascend(p("id"))),
    f(C(includes(__,m(head,props.activity.inputs)),p("activity_id")))
  )(props.data || []) 

  return <Activity {...props} 
    items={items}
    items2={items}
    responded={responded(props.activity,props)}
    filtered={filter_data(props.activity,props)}/>
}

const splitDoneAndNext = curry((d,d_i,fn,xs) => {
  const index = findIndex(fn, xs);
  return (index === -1 || d_i === null)  ? 
    ((d < Math.min(...xs.map(([x]) => x))) ? [slice(0, 1, xs),slice(1,Infinity,xs)] : [xs, []]) :
    [slice(0, index + 2, xs), slice(index + 2, Infinity, xs)];
});

export const OneByOne = (props) => {
  const [openIndex, setOpenIndex] = useState(0); 
  const input_id = C( 
    Number,head,reduce(maxBy(last),[0]),toPairs,//take most common
    countBy(id),f(is(Number)),m(head),chain(p("inputs")), //count keys
    p("activities_with_types"))(props.part)
      
  const items = C(
    sort(ascend(p("id"))),
    f(x => x.activity_id === input_id)
  )(props.data || [])
      
  const items2 = (a,props,item)=> C(
    sort(ascend(p("id"))),
    f(propEq("input_id",item)),
    f(C(includes(__,m(head,a.inputs)),p("activity_id")))
  )(props.data)

  const [visible_items,to_do_items] = C(
    splitDoneAndNext(props.done,props.done_item,([d,d_i]) => d+"_"+d_i === props.done+"_"+props.done_item),
    chain(i => m(x => ([x.order_nr,i.id]),props.part.activities_with_types)),   
  )(items) 
  const visible_obj = C(
    m(m(head)),
    g(last))(visible_items)

  useEffect(() => {
    C(setOpenIndex,dec,length,keys)(visible_obj)
  },[props.show_until])

  return <Accordion index={openIndex} colorScheme="teal">
    {items.map((item,n) => 
      <AccordionItem items={item} key={item.id}>
        <Heading as="h2">
          <AccordionButton onClick={() => setOpenIndex(n)} fontWeight={openIndex === n ? "bold" : "normal"} >
            {item.value_text}
            <AccordionIcon />
          </AccordionButton>
        </Heading>
        <AccordionPanel p={4} key={item.id}>{C(
          addIndex(m)((a,n2,xs) => 
            <Activity {...props} 
              order_nr={a.order_nr}
              key={item.id+"_"+a.id}
              data_input={item.id}
              next_done_item={to_do_items.length ? item.id : null}
              next_to_show={to_do_items.length ? to_do_items[0][0] : props.next_to_show}
              items={[item]}
              next_btn_visible={
                (last(visible_items)[0] === a.order_nr) && 
                last(visible_items)[1] === item.id}
              items2={items2(a,props,item.id)} 
              activity={a}
              responded={responded(a,props,item.id)}
              filtered={filter_data(a,props,item.id)}/>),
          f(x => visible_obj[item.id]?.includes(x.order_nr)),
          sort(ascend(p("id")))
        )(props.part.activities_with_types || [])}
        </AccordionPanel>
      </AccordionItem>
    )}
  </Accordion>
}

export const Part = (props) => {
  const answers_needed = props.participants
    .filter(x => x.participant_roles
      .map(x => x.role_id)
      .includes(props.part.role_id))
    .length
  return !props.data ? null : <Box shadow="md" mt={20} p={5} borderRadius={10} bg={'gray.50'} >
    <PartHeader props={props}></PartHeader>
    {props.part.one_by_one ? <OneByOne {...props} answers_needed={answers_needed}/> :
      C(
        addIndex(m)((a,n,xs) => {
          return ((props.show_until >= a.order_nr) && props.skippable[a.order_nr] !== false) ? 
          <Regular {...props} 
            is_last={(xs.length-1) === n && props.last_part}
            answers_needed={answers_needed}
            order_nr={a.order_nr} 
            key={a.id} 
            activity={a} 
          /> : null}),
        sort(ascend(p("order_nr")))
      )(props.part.activities_with_types)}
  </Box>
}